import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Activity, CalendarRange, Filter, ArrowLeft, RefreshCcw, Search } from "lucide-react";
import { getActivityLogs } from "../api/activity";
import {
  resolveActivityActorName,
  resolveActivitySubjectName,
} from "../utils/activityDisplay";
import "./EmployeeActivity.css";

const formatDateTime = (value) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};

export default function EmployeeActivityDetail() {
  const navigate = useNavigate();
  const { employeeId } = useParams();
  const location = useLocation();
  const [activityHistory, setActivityHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [employeeName, setEmployeeName] = useState(
    location.state?.employeeName || "Employee"
  );
  const [filters, setFilters] = useState({
    module: "",
    action: "",
    dateFrom: "",
    dateTo: "",
  });

  useEffect(() => {
    if (!employeeId) {
      setError("No employee selected for activity history.");
      return;
    }
    loadHistory(filters);
  }, [employeeId]);

  const loadHistory = async (nextFilters = filters) => {
    setLoading(true);
    setError("");

    try {
      const data = await getActivityLogs({
        employeeId,
        module: nextFilters.module,
        action: nextFilters.action,
        dateFrom: nextFilters.dateFrom,
        dateTo: nextFilters.dateTo,
        limit: 1000,
      });

      setActivityHistory(data || []);
      if (!location.state?.employeeName && data?.length > 0) {
        const first = data[0];
        setEmployeeName(resolveActivitySubjectName(first) || "Employee");
      }
    } catch (err) {
      console.error("Failed to fetch employee activity details:", err);
      setError(
        err?.response?.data?.message || "Unable to load employee activity history."
      );
      setActivityHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const applyFilters = (event) => {
    event.preventDefault();
    loadHistory(filters);
  };

  const clearFilters = () => {
    const reset = {
      module: "",
      action: "",
      dateFrom: "",
      dateTo: "",
    };
    setFilters(reset);
    loadHistory(reset);
  };

  return (
    <div className="employee-activity-page page-container">
      <header className="employee-activity-header">
        <div>
          <button
            type="button"
            className="employee-activity-back-btn"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft size={18} />
            Back
          </button>
          <h1 className="employee-activity-title">
            <Activity size={24} strokeWidth={2.2} />
            {employeeName} Activity
          </h1>
          <p className="employee-activity-subtitle">
            Full history for the selected employee, sorted latest first.
          </p>
        </div>
        <div className="employee-activity-summary">
          <span>Total Activities</span>
          <strong>{activityHistory.length}</strong>
        </div>
      </header>

      <section className="employee-activity-card">
        <div className="employee-activity-card-head">
          <div>
            <h2>Filters</h2>
            <p>Filter the employee history by module, action, or date range.</p>
          </div>
        </div>

        <form className="employee-activity-filters" onSubmit={applyFilters}>
          <label className="employee-activity-field">
            <span>
              <Filter size={14} />
              Module
            </span>
            <input
              type="text"
              name="module"
              value={filters.module}
              onChange={handleFilterChange}
              placeholder="Inventory, Auth, NetWorth"
            />
          </label>

          <label className="employee-activity-field">
            <span>
              <Filter size={14} />
              Action
            </span>
            <input
              type="text"
              name="action"
              value={filters.action}
              onChange={handleFilterChange}
              placeholder="Created, Updated, Deleted"
            />
          </label>

          <label className="employee-activity-field">
            <span>
              <CalendarRange size={14} />
              From
            </span>
            <input
              type="date"
              name="dateFrom"
              value={filters.dateFrom}
              onChange={handleFilterChange}
            />
          </label>

          <label className="employee-activity-field">
            <span>
              <CalendarRange size={14} />
              To
            </span>
            <input
              type="date"
              name="dateTo"
              value={filters.dateTo}
              onChange={handleFilterChange}
            />
          </label>

          <div className="employee-activity-filter-actions">
            <button type="submit" className="employee-activity-btn is-primary">
              <Search size={16} />
              Apply Filters
            </button>
            <button
              type="button"
              className="employee-activity-btn"
              onClick={clearFilters}
            >
              Reset
            </button>
            <button
              type="button"
              className="employee-activity-btn"
              onClick={() => loadHistory(filters)}
            >
              <RefreshCcw size={16} />
              Refresh
            </button>
          </div>
        </form>
      </section>

      <section className="employee-activity-card">
        <div className="employee-activity-card-head">
          <div>
            <h2>Activity Timeline</h2>
            <p>{activityHistory.length} activity records</p>
          </div>
        </div>

        {error ? <div className="employee-activity-state is-error">{error}</div> : null}

        <div className="employee-activity-table-wrap">
          <table className="employee-activity-table">
            <thead>
              <tr>
                <th>Date & Time</th>
                <th>Action</th>
                <th>Module</th>
                <th>Description</th>
                <th>Actor</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="5" className="employee-activity-empty">
                    Loading history...
                  </td>
                </tr>
              ) : activityHistory.length === 0 ? (
                <tr>
                  <td colSpan="5" className="employee-activity-empty">
                    No history found for this employee.
                  </td>
                </tr>
              ) : (
                activityHistory.map((entry) => (
                  <tr key={entry.id}>
                    <td>{formatDateTime(entry.timestamp)}</td>
                    <td>
                      <span className="employee-activity-chip">{entry.action || "-"}</span>
                    </td>
                    <td>{entry.module || "-"}</td>
                    <td>{entry.description || "-"}</td>
                    <td>{resolveActivityActorName(entry) || "-"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
